import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  balikovnaPickupSelectors,
  DeliveryMethod,
} from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

const schema = z.object({
  balikovnaPickupSelector: z.enum(balikovnaPickupSelectors),
})

type BalikovnaSettingsFormValues = z.infer<typeof schema>

interface BalikovnaDeliverySettingsFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  method: DeliveryMethod | null
  onSuccess: () => void
}

export function BalikovnaDeliverySettingsForm({
  open,
  onOpenChange,
  method,
  onSuccess,
}: BalikovnaDeliverySettingsFormProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.deliveryMethods.balikovnaSettings',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const form = useForm<BalikovnaSettingsFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      balikovnaPickupSelector: 'feed',
    },
  })

  useEffect(() => {
    if (!open || !method) return
    form.reset({
      balikovnaPickupSelector: method.balikovnaPickupSelector ?? 'feed',
    })
  }, [open, method, form])

  if (!method || method.type !== 'balikovna') return null

  const onSubmit = async (values: BalikovnaSettingsFormValues) => {
    try {
      await API.patch('/api/v2/delivery-methods/balikovna', {
        balikovnaPickupSelector: values.balikovnaPickupSelector,
      })
      toast.success(t('success'))
      onOpenChange(false)
      onSuccess()
    } catch (error: unknown) {
      toast.error(t('error'), {
        description: extractErrorMessage(error),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="balikovnaPickupSelector"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3">
                  <FormLabel>{t('fields.selector.label')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      className="flex flex-col gap-3"
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormItem className="flex items-start space-x-2 space-y-0 rounded-lg border p-3">
                        <FormControl>
                          <RadioGroupItem value="feed" id="balikovna-feed" />
                        </FormControl>
                        <div className="flex flex-col gap-1">
                          <FormLabel
                            htmlFor="balikovna-feed"
                            className="font-medium"
                          >
                            {t('fields.selector.options.feed')}
                          </FormLabel>
                          <FormDescription>
                            {t('fields.selector.descriptions.feed')}
                          </FormDescription>
                        </div>
                      </FormItem>
                      <FormItem className="flex items-start space-x-2 space-y-0 rounded-lg border p-3">
                        <FormControl>
                          <RadioGroupItem
                            value="widget"
                            id="balikovna-widget"
                          />
                        </FormControl>
                        <div className="flex flex-col gap-1">
                          <FormLabel
                            htmlFor="balikovna-widget"
                            className="font-medium"
                          >
                            {t('fields.selector.options.widget')}
                          </FormLabel>
                          <FormDescription>
                            {t('fields.selector.descriptions.widget')}
                          </FormDescription>
                        </div>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {te('actions.cancel')}
              </Button>
              <Button type="submit">{te('actions.save')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
